import { Button, Header, Icon, Message } from "semantic-ui-react";
import React, { ComponentType, useContext } from "react";
import { User, useAuth0 } from "@auth0/auth0-react";

import { AuthContext } from "./AuthContextProvider";
import { Role } from "../types";

export interface WithAuthorizationRequiredProps {
    allowIfHasRole?: Role;
    allowIfHasAnyRole?: Role[];
    claimCheck?: (user: User, roles: Role[]) => boolean;
    fallback?: React.FC;
}

const defaultClaimCheck = () => true;

interface DefaultFallbackProps {
    allowIfHasRole?: Role;
    allowIfHasAnyRole?: Role[];
}

const DefaultFallback: React.FC<DefaultFallbackProps> = (props: DefaultFallbackProps) => {
    const { allowIfHasRole, allowIfHasAnyRole } = props;
    const { logout } = useAuth0();
    return (
        <Message error>
            <Message.Header content="Error" />
            <br />
            Missing one or more required roles: <strong>{allowIfHasAnyRole?.join(", ") || allowIfHasRole}</strong>
            <br />
            <br />
            You are not authorized to view this page. Please contact the helpdesk for assistance.
            <br />
            <br />
            <Button compact type="button" color="grey" content="Sign out" onClick={() => logout()} />
        </Message>
    );
};

const withAuthorizationRequired = <P extends object>(Component: ComponentType<P>, options: WithAuthorizationRequiredProps): React.FC<P> => {
    const { allowIfHasRole, allowIfHasAnyRole, claimCheck = defaultClaimCheck, fallback = DefaultFallback } = options;

    return function WithAuthorizationRequired(props: P) {
        const { isAuthenticated, user } = useAuth0();
        const { userRoles, userHasRole, userHasAnyRole } = useContext(AuthContext);
        if (!isAuthenticated || !user) {
            return fallback(props);
        }

        if (!userRoles) {
            return <Header icon={<Icon name="cog" loading />} content="Checking user permissions..." color="grey" />;
        }

        if (!claimCheck(user, userRoles)) {
            // seems this will always be true
            return fallback(props);
        }

        if (!!allowIfHasRole && !userHasRole(allowIfHasRole)) {
            return fallback({ allowIfHasRole });
        }

        if (!!allowIfHasAnyRole && !userHasAnyRole(allowIfHasAnyRole)) {
            return fallback({ allowIfHasAnyRole });
        }

        return <Component {...props} />;
    };
};

export default withAuthorizationRequired;
